/**
 * Unsplash API服务
 * 用于获取高质量的免费图片
 */

const UNSPLASH_API_URL = 'https://api.unsplash.com';
const UNSPLASH_ACCESS_KEY = process.env.REACT_APP_UNSPLASH_ACCESS_KEY || '';

// 调试：检查环境变量是否加载
if (process.env.NODE_ENV === 'development') {
  console.log('环境变量检查:', {
    hasKey: !!UNSPLASH_ACCESS_KEY,
    keyLength: UNSPLASH_ACCESS_KEY.length,
    keyPreview: UNSPLASH_ACCESS_KEY ? `${UNSPLASH_ACCESS_KEY.substring(0, 10)}...` : 'empty'
  });
}

// 默认的加密货币/区块链相关搜索关键词
const DEFAULT_QUERIES = [
  'cryptocurrency blockchain',
  'bitcoin ethereum',
  'digital currency',
  'crypto trading',
  'blockchain technology',
  'cryptocurrency investment'
];

const getImageCacheKey = (image) => (
  typeof image === 'string'
    ? image
    : image.id || image.url || image.fullUrl || image.thumbUrl
);

const dedupeImages = (images) => {
  const seen = new Set();

  return images.filter((image) => {
    const key = getImageCacheKey(image);
    if (!key || seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

/**
 * 搜索图片
 * @param {string} query - 搜索关键词
 * @param {number} perPage - 每页数量
 * @returns {Promise<Array>} 图片URL数组
 */
export const searchImages = async (query = 'cryptocurrency blockchain', perPage = 6) => {
  if (!UNSPLASH_ACCESS_KEY) {
    console.warn('Unsplash Access Key未配置，使用默认图片');
    return getDefaultImages();
  }

  try {
    const response = await fetch(
      `${UNSPLASH_API_URL}/search/photos?query=${encodeURIComponent(query)}&per_page=${perPage}&orientation=landscape&client_id=${UNSPLASH_ACCESS_KEY}`
    );

    if (!response.ok) {
      throw new Error(`Unsplash API错误: ${response.status}`);
    }

    const data = await response.json();
    
    if (data.results && data.results.length > 0) {
      const images = data.results.map(photo => ({
        id: photo.id,
        url: photo.urls?.regular || photo.urls?.small || '',
        fullUrl: photo.urls?.full || photo.urls?.regular || '',
        thumbUrl: photo.urls?.thumb || photo.urls?.small || '',
        description: photo.description || photo.alt_description || '',
        author: photo.user?.name || 'Unsplash',
        authorUrl: photo.user?.links?.html || 'https://unsplash.com'
      })).filter(img => img.url); // 过滤掉没有URL的图片
      
      if (images.length > 0) {
        console.log(`✅ 成功获取 ${images.length} 张图片`);
        return images;
      }
    }

    console.warn('Unsplash API返回空结果，使用默认图片');
    return getDefaultImages();
  } catch (error) {
    console.error('获取Unsplash图片失败:', error);
    return getDefaultImages();
  }
};

/**
 * 获取多组图片（使用不同的搜索关键词）
 * @param {number} count - 需要的图片数量
 * @returns {Promise<Array>} 图片URL数组
 */
export const getMultipleImageSets = async (count = 6) => {
  if (!UNSPLASH_ACCESS_KEY) {
    console.warn('Unsplash Access Key未配置，使用默认图片');
    return getDefaultImages();
  }

  try {
    // 尝试从不同的关键词获取图片
    const queries = DEFAULT_QUERIES.slice(0, Math.ceil(count / 2));
    const allImages = [];

    for (const query of queries) {
      try {
        const images = await searchImages(query, 2);
        allImages.push(...images);
        if (allImages.length >= count) {
          break;
        }
      } catch (error) {
        console.warn(`搜索关键词 "${query}" 失败:`, error);
        continue;
      }
    }

    const uniqueImages = dedupeImages(allImages);

    // 如果获取的图片不够，使用默认图片补充
    if (uniqueImages.length < count) {
      const defaultImages = getDefaultImages();
      uniqueImages.push(...defaultImages.slice(0, count - uniqueImages.length));
    }

    return uniqueImages.slice(0, count);
  } catch (error) {
    console.error('获取多组图片失败:', error);
    return getDefaultImages();
  }
};

/**
 * 获取默认图片（当API不可用时使用）
 * @returns {Array} 默认图片URL数组
 */
export const getDefaultImages = () => {
  // 使用稳定的图片URL（通过代理或直接可用的CDN）
  // 这些是经过测试可用的图片URL
  return [
    {
      id: 'default-1',
      url: 'https://images.pexels.com/photos/6770615/pexels-photo-6770615.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop&dpr=1',
      fullUrl: 'https://images.pexels.com/photos/6770615/pexels-photo-6770615.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop&dpr=1',
      thumbUrl: 'https://images.pexels.com/photos/6770615/pexels-photo-6770615.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop',
      description: 'Cryptocurrency',
      author: 'Pexels',
      authorUrl: 'https://pexels.com'
    },
    {
      id: 'default-2',
      url: 'https://images.pexels.com/photos/6801874/pexels-photo-6801874.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop&dpr=1',
      fullUrl: 'https://images.pexels.com/photos/6801874/pexels-photo-6801874.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop&dpr=1',
      thumbUrl: 'https://images.pexels.com/photos/6801874/pexels-photo-6801874.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop',
      description: 'Blockchain',
      author: 'Pexels',
      authorUrl: 'https://pexels.com'
    },
    {
      id: 'default-3',
      url: 'https://images.pexels.com/photos/6801648/pexels-photo-6801648.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop&dpr=1',
      fullUrl: 'https://images.pexels.com/photos/6801648/pexels-photo-6801648.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop&dpr=1',
      thumbUrl: 'https://images.pexels.com/photos/6801648/pexels-photo-6801648.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop',
      description: 'Digital Currency',
      author: 'Pexels',
      authorUrl: 'https://pexels.com'
    },
    {
      id: 'default-4',
      url: 'https://images.pexels.com/photos/6801876/pexels-photo-6801876.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop&dpr=1',
      fullUrl: 'https://images.pexels.com/photos/6801876/pexels-photo-6801876.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop&dpr=1',
      thumbUrl: 'https://images.pexels.com/photos/6801876/pexels-photo-6801876.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop',
      description: 'Crypto Trading',
      author: 'Pexels',
      authorUrl: 'https://pexels.com'
    },
    {
      id: 'default-5',
      url: 'https://images.pexels.com/photos/6801875/pexels-photo-6801875.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop&dpr=1',
      fullUrl: 'https://images.pexels.com/photos/6801875/pexels-photo-6801875.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop&dpr=1',
      thumbUrl: 'https://images.pexels.com/photos/6801875/pexels-photo-6801875.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop',
      description: 'Blockchain Technology',
      author: 'Pexels',
      authorUrl: 'https://pexels.com'
    },
    {
      id: 'default-6',
      url: 'https://images.pexels.com/photos/6801647/pexels-photo-6801647.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop&dpr=1',
      fullUrl: 'https://images.pexels.com/photos/6801647/pexels-photo-6801647.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop&dpr=1',
      thumbUrl: 'https://images.pexels.com/photos/6801647/pexels-photo-6801647.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop',
      description: 'Cryptocurrency Investment',
      author: 'Pexels',
      authorUrl: 'https://pexels.com'
    }
  ];
};

/**
 * 缓存图片到localStorage
 * @param {Array} images - 图片数组
 */
export const cacheImages = (images) => {
  try {
    const cacheData = {
      images,
      timestamp: Date.now(),
      expiresIn: 24 * 60 * 60 * 1000 // 24小时
    };
    localStorage.setItem('unsplash_images_cache', JSON.stringify(cacheData));
  } catch (error) {
    console.warn('缓存图片失败:', error);
  }
};

/**
 * 从缓存获取图片
 * @returns {Array|null} 缓存的图片数组或null
 */
export const getCachedImages = () => {
  try {
    const cached = localStorage.getItem('unsplash_images_cache');
    if (!cached) return null;

    const cacheData = JSON.parse(cached);
    const now = Date.now();

    // 检查是否过期
    if (now - cacheData.timestamp > cacheData.expiresIn) {
      localStorage.removeItem('unsplash_images_cache');
      return null;
    }

    return Array.isArray(cacheData.images) ? dedupeImages(cacheData.images) : null;
  } catch (error) {
    console.warn('读取缓存失败:', error);
    return null;
  }
};
